import { CameraController } from './camera-controller';
import { EventHub } from './event-hub';
import { ExtensionManager } from './extension-manager';
import { ForgeViewerEvents } from './forge-viewer.enums';
import { EventHubListener, ForgeViewerOptions } from './forge-viewer.types';
import { ModelManager } from './model-manager';
import { OverlayManager } from './overlay-manager';
import { ViewerCore } from './viewer-core';

export class ForgeViewer {
    private options: ForgeViewerOptions;
    private core: ViewerCore;
    private eventHub: EventHub;
    private modelManager?: ModelManager;
    private cameraController?: CameraController;
    private extensionManager?: ExtensionManager;
    private overlayManager?: OverlayManager;
    private viewerEventListeners: Array<{ event: string; handler: (e: any) => void }> = [];

    constructor(container: HTMLElement, options: ForgeViewerOptions) {
        this.options = options;
        this.core = new ViewerCore(container, options);
        this.eventHub = new EventHub();
    }

    get instance(): Autodesk.Viewing.GuiViewer3D | null {
        return this.core.instance;
    }

    get camera(): CameraController | undefined {
        return this.cameraController;
    }

    get extensions(): ExtensionManager | undefined {
        return this.extensionManager;
    }

    get overlays(): OverlayManager | undefined {
        return this.overlayManager;
    }

    get model(): ModelManager | undefined {
        return this.modelManager;
    }

    // Event API
    on(event: ForgeViewerEvents | string, fn: EventHubListener<any>) {
        this.eventHub.on(event, fn);
    }

    off(event: ForgeViewerEvents | string, fn?: EventHubListener<any>) {
        this.eventHub.off(event, fn);
    }

    // init loads scripts, initializes viewer and optionally loads model
    async init(loadModel = true): Promise<void> {
        await this.core.init();

        const viewer = this.core.instance!;

        const profileSettings = {
            name: 'progresscenter-settings',
            settings: {
                viewCube: false,
                alwaysUsePivot: true,
                zoomTowardsPivot: true,
                reverseHorizontalLookDirection: true,
                reverseVerticalLookDirection: true,
                // orbitPastWorldPoles: false,
                bimWalkToolPopup: false,
                reverseMouseZoomDir: true,
                // leftHandedMouseSetup: true,
                fusionOrbitConstrained: true,
                wheelSetsPivot: true,
            },
            extensions: {
                load: [] as any, // Extension IDs
                unload: [] as any, // Extension IDs
            },
        };

        // const profile = new (Autodesk.Viewing as any).Profile(profileSettings);
        // (viewer as any).setProfile(profile);

        for (const [key, value] of Object.entries(profileSettings.settings)) {
            viewer.prefs.set(key, value);
        }

        this.cameraController = new CameraController(viewer);
        this.extensionManager = new ExtensionManager(viewer);
        this.overlayManager = new OverlayManager(viewer);
        this.modelManager = new ModelManager(viewer);

        this.hideViewCubeWhenReady();

        this.registerViewerEventListeners(viewer);

        if (loadModel && this.options.urn) {
            await this.modelManager.load(this.options.urn);
        }
    }

    showDbIds(dbIds: number[]) {
        this.instance!.show(dbIds);
    }

    hideDbIds(dbIds: number[]) {
        this.instance!.hide(dbIds);
    }

    refreshScene() {
        this.instance!.impl.sceneUpdated(true);
    }

    private registerViewerEventListeners(viewer: Autodesk.Viewing.GuiViewer3D) {
        // Event mapping: Autodesk event -> ForgeViewerEvents
        const eventMap: Record<string, { forgeEvent: ForgeViewerEvents; withExtension?: boolean }> = {
            [Autodesk.Viewing.VIEWER_INITIALIZED]: { forgeEvent: ForgeViewerEvents.INITIALIZED },
            [Autodesk.Viewing.MODEL_ADDED_EVENT]: { forgeEvent: ForgeViewerEvents.MODEL_ADDED },
            [Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT]: { forgeEvent: ForgeViewerEvents.OBJECT_TREE_CREATED },
            [Autodesk.Viewing.TEXTURES_LOADED_EVENT]: { forgeEvent: ForgeViewerEvents.TEXTURES_LOADED },
            [Autodesk.Viewing.GEOMETRY_LOADED_EVENT]: { forgeEvent: ForgeViewerEvents.GEOMETRY_LOADED },
            [Autodesk.Viewing.TOOL_CHANGE_EVENT]: { forgeEvent: ForgeViewerEvents.TOOL_CHANGE },
            [Autodesk.Viewing.VIEWER_RESIZE_EVENT]: { forgeEvent: ForgeViewerEvents.VIEWER_RESIZE },
            [Autodesk.Viewing.EXTENSION_LOADED_EVENT]: {
                forgeEvent: ForgeViewerEvents.EXTENSION_LOADED,
                withExtension: true,
            },
            [Autodesk.Viewing.EXTENSION_ACTIVATED_EVENT]: {
                forgeEvent: ForgeViewerEvents.EXTENSION_ACTIVATED,
                withExtension: true,
            },
            [Autodesk.Viewing.EXTENSION_DEACTIVATED_EVENT]: {
                forgeEvent: ForgeViewerEvents.EXTENSION_DEACTIVATED,
                withExtension: true,
            },
        };

        for (const [autodeskEvent, config] of Object.entries(eventMap)) {
            const handler = (e: any) => {
                const payload = e;
                if (config.withExtension && e.extensionId) {
                    payload.extension = viewer.getExtension(e.extensionId);
                }
                this.eventHub.emit(config.forgeEvent, payload);
            };

            viewer.addEventListener(autodeskEvent, handler);
            this.viewerEventListeners.push({ event: autodeskEvent, handler });
        }
    }

    private removeViewerEventListeners() {
        if (!this.instance) return;

        for (const { event, handler } of this.viewerEventListeners) {
            this.instance.removeEventListener(event, handler);
        }
        this.viewerEventListeners = [];
    }

    private hideViewCubeWhenReady() {
        const observer = new MutationObserver(() => {
            const viewcubeWrapper = document.querySelector('.viewcubeWrapper') as HTMLDivElement;

            if (viewcubeWrapper) {
                // Element found → hide or remove
                // viewcubeWrapper.style.display = 'none';
                viewcubeWrapper.remove();

                observer.disconnect(); // stop listening
            }
        });

        // Observe DOM changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    // destroy everything
    destroy() {
        this.removeViewerEventListeners();
        this.modelManager?.unload();
        this.core.shutdown();
        this.eventHub.emit(ForgeViewerEvents.DESTROYED);
    }
}
